import { useState, useEffect, useRef, createContext, useContext, type ReactNode } from "react";
import { WebsocketProvider } from "y-websocket";
import { v4 as uuidv4 } from "uuid";
import * as Y from "yjs";
import { useAccount } from "../../hooks/use-account";
import { useDatadog } from "../../hooks/use-datadog";
import { useGlobal } from "../../hooks/use-global";
import { User } from "../../types/dbTypes";
import { convertLocalTimeToServer } from "../../utils/time";
import { getColorForUserName } from "../../utils/getColorForUserName";

export interface Comment {
  id?: string;
  updatedAt?: string | Date;
  owner?: Partial<User>;
  fromRel?: any;
  toRel?: any;
  refText?: string;
  text: string;
  subComments?: string[];
  parentComment?: string;
  isResolved?: boolean;
  isDeleted?: boolean;
  isOrphaned?: boolean;
}

export interface CommentRef {
  id?: string;
  fromRel: any;
  toRel: any;
  fromAbs: number;
  toAbs: number;
  refText: string;
}

export const useCollaborativeProvider = ({
  roomName,
  isDisabled
}: ICollaborativeProps) => {
  const { pingData } = useGlobal();
  const { latestToken, vojomoAccount } = useAccount();
  const { trackEvent } = useDatadog();
  const [ydoc, setYdoc] = useState(new Y.Doc());
  const [status, setStatus] = useState<string>(null);
  const [provider, setProvider] = useState(null);
  const [isSynced, setIsSynced] = useState<boolean>(null);
  const [inactivityDisc, setInactivityDisc] = useState<boolean>(false);
  const [isMasterClient, setIsMasterClient] = useState<boolean>(false);
  const [isPendingSave, setIsPendingSave] = useState<boolean>(false);

  const [localComments, setLocalComments] = useState<Comment[]>([]);
  const [newComments, setNewComments] = useState<string[]>([]);

  const [activeComment, setActiveComment] = useState<Comment>(null);

  const [commentsInEditMode, setCommentsInEditMode] = useState<string[]>([]);

  const lastActivityRef = useRef(Date.now());
  const disconnectedRef = useRef(false);

  useEffect(() => {
    console.log("Mounted Collab Provider");

    return () => {
      console.log("Unmounting Collab Provider");
    };
  }, []);

  const createComment = ({
    parentId,
    text,
    refText,
    fromAbs,
    toAbs,
    fromRel,
    toRel
  }) => {
    const newCommentId = uuidv4();

    const currentServerTime = convertLocalTimeToServer(
      new Date(Date.now()),
      new Date(pingData.svTime)
    );

    const newComment = new Y.Map<any>([
      ["parentComment", parentId],
      [
        "owner",
        {
          id: vojomoAccount["_id"],
          name: vojomoAccount.name,
          email: vojomoAccount.email
        }
      ],
      ["text", text],
      ["createdAt", currentServerTime.toISOString()],
      ["updatedAt", currentServerTime.toISOString()],
      ["id", newCommentId],
      ["subComments", new Y.Array()],
      ["isDeleted", false],
      ["isOrphaned", false],
      ["isResolved", false]
    ]);

    if (fromRel && toRel) {
      newComment.set("fromRel", Y.encodeRelativePosition(fromRel));
      newComment.set("toRel", Y.encodeRelativePosition(toRel));
    }

    const yMap = ydoc.getMap("comments");
    const yCommentsRef = ydoc.getMap("commentsRef");

    ydoc.transact(() => {
      if (parentId) {
        const parentComment = yMap.get(parentId) as any;
        const sc = parentComment.get("subComments");

        sc.push([newCommentId]);
      }

      const commentRefMap = new Y.Map();

      commentRefMap.set("refText", refText);
      commentRefMap.set("from", fromAbs);
      commentRefMap.set("to", toAbs);

      yCommentsRef.set(newCommentId, commentRefMap);
      yMap.set(newCommentId, newComment);
    });

    if (!parentId) {
      setCommentsInEditMode((prev) => {
        const newArray = [...prev];
        newArray.push(newCommentId);

        return newArray;
      });
    }

    trackEvent("created_comment", {
      newComment: Object.fromEntries(newComment),
      roomName
    });
  };

  const getComment = (id: string) => {
    return localComments.find((c) => c.id === id);
  };

  const updateComment = (commentId: string, text: string) => {
    const yMap = ydoc.getMap("comments");

    ydoc.transact(() => {
      const comment = yMap.get(commentId) as Y.Map<any>;
      comment.set("text", text);
      comment.set(
        "updatedAt",
        convertLocalTimeToServer(
          new Date(Date.now()),
          new Date(pingData.svTime)
        ).toISOString()
      );
    });

    const localComment = getComment(commentId);
    trackEvent("updated_comment", {
      updatedComment: localComment,
      newText: text,
      roomName
    });
  };

  const resolveComment = (commentId: string, isResolved: boolean) => {
    const yMap = ydoc.getMap("comments");

    ydoc.transact(() => {
      const comment = yMap.get(commentId) as Y.Map<any>;
      if (!comment) return;
      comment.set("isResolved", isResolved);
      comment.set(
        "updatedAt",
        convertLocalTimeToServer(
          new Date(Date.now()),
          new Date(pingData.svTime)
        ).toISOString()
      );
    });

    trackEvent("resolved_comment", { commentId, isResolved, roomName });
  };

  const deleteComments = (commentIds: string[]) => {
    const yMap = ydoc.getMap("comments");

    ydoc.transact(() => {
      const toMarkAsDeleted: string[] = [];

      for (const commentId of commentIds) {
        toMarkAsDeleted.push(commentId);

        const comment = yMap.get(commentId) as Y.Map<any>;
        const scYarray = comment.get("subComments") as Y.Array<string>;

        if (scYarray.length > 0) {
          for (const scId of scYarray) {
            toMarkAsDeleted.push(scId);
          }
        }
      }

      for (const id of toMarkAsDeleted) {
        if (yMap.has(id)) {
          const comment = yMap.get(id) as Y.Map<any>;
          comment.set("isDeleted", true);
        }
      }
    });

    const localCommentsToDelete = [];

    for (const id of commentIds) {
      const c = getComment(id);

      if (c) {
        localCommentsToDelete.push(c);
      }
    }

    trackEvent("deleted_comments", {
      localCommentsToDelete,
      roomName
    });
  };

  const updateCommentRef = (
    commentId: string,
    refText: string,
    from: number,
    to: number
  ) => {
    const yCommentsRef = ydoc.getMap("commentsRef");
    const commentRef = yCommentsRef.get(commentId) as Y.Map<any>;

    if (
      commentRef.get("refText") !== refText ||
      commentRef.get("from") !== from ||
      commentRef.get("to") !== to
    ) {
      ydoc.transact(() => {
        commentRef.set("refText", refText);
        commentRef.set("from", from);
        commentRef.set("to", to);
      });
    }
  };

  const updateOrphanedComments = (
    commentsData: { id: string; payload: {} }[]
  ) => {
    const yMap = ydoc.getMap("comments");

    ydoc.transact(() => {
      const toUpdateOrphanage: { id: string; payload: {} }[] = [];

      for (const { id, payload } of commentsData) {
        toUpdateOrphanage.push({ id, payload });

        const comment = yMap.get(id) as Y.Map<any>;
        const scYarray = comment.get("subComments") as Y.Array<string>;

        if (scYarray.length > 0) {
          for (const scId of scYarray) {
            toUpdateOrphanage.push({ id: scId, payload });
          }
        }
      }

      for (const { id, payload } of toUpdateOrphanage) {
        if (yMap.has(id)) {
          const comment = yMap.get(id) as Y.Map<any>;

          for (const key in payload) {
            comment.set(key, payload[key]);
          }
        }
      }
    });
  };

  useEffect(() => {
    if (latestToken && roomName && ydoc && !isDisabled) {
      const wsProvider = new WebsocketProvider(
        import.meta.env.VITE_REACT_APP_YWS,
        roomName,
        ydoc,
        {
          resyncInterval: 5000,
          WebSocketPolyfill: class extends WebSocket {
            constructor(url, _) {
              super(url, `Bearer.${latestToken}`);
            }
          }
        }
      );

      wsProvider.on("status", (event) => {
        setStatus(event.status);
      });

      // Handle connection errors
      wsProvider.on("connection-error", (error) => {
        console.error("WebSocket connection error:", error);
      });

      wsProvider.on("sync", (event) => {
        setIsSynced(event);
      });

      // Handle connection close
      wsProvider.on("connection-close", (event) => {
        console.log("WebSocket connection closed:", event);
      });

      setProvider(wsProvider);

      return () => {
        console.log("Destroying");

        setLocalComments([]);
        wsProvider?.destroy();
        ydoc.destroy();
      };
    }
  }, [latestToken, ydoc, roomName, isDisabled]);

  useEffect(() => {
    if (!isSynced && !inactivityDisc) {
      ydoc?.destroy();
      setYdoc(new Y.Doc());
    }
  }, [isSynced, inactivityDisc]);

  useEffect(() => {
    if (provider) {
      const activityEvents = [
        "mousemove",
        "keydown",
        "mousedown",
        "touchstart"
      ];

      const INACTIVITY_LIMIT = 5 * 60 * 1000; // 5 minutes

      const checkInactivity = () => {
        if (
          Date.now() - lastActivityRef.current > INACTIVITY_LIMIT &&
          provider.wsconnected
        ) {
          setInactivityDisc(true);
          provider.disconnect();
          disconnectedRef.current = true;
          console.log("User disconnected due to inactivity");
        }
      };

      const updateActivity = () => {
        lastActivityRef.current = Date.now();
        setInactivityDisc(false);

        if (disconnectedRef.current && !provider.wsconnected) {
          provider.connect();
          disconnectedRef.current = false;
          console.log("User reconnected due to activity");
        }
      };

      activityEvents.forEach((event) =>
        window.addEventListener(event, updateActivity)
      );

      const interval = setInterval(checkInactivity, INACTIVITY_LIMIT / 2);

      return () => {
        console.log("Calling cleanup");
        clearInterval(interval);
        activityEvents.forEach((event) =>
          window.removeEventListener(event, updateActivity)
        );
      };
    }
  }, [provider]);

  useEffect(() => {
    if (isSynced) {
      const meta = ydoc.getMap("meta");

      const updateMeta = () => {
        setIsPendingSave(!!meta.get("isSaving"));
        setIsMasterClient(meta.get("masterClientId") === vojomoAccount["_id"]);
      };

      updateMeta();
      meta.observeDeep(updateMeta);

      return () => {
        meta.unobserveDeep(updateMeta);
      };
    }
  }, [ydoc, isSynced]);

  useEffect(() => {
    if (provider) {
      provider.awareness.setLocalStateField("user", {
        id: vojomoAccount["_id"],
        name: vojomoAccount["name"],
        color: getColorForUserName(vojomoAccount["name"])
      });
    }

    if (!provider?.awareness) return;

    const handleBeforeUnload = () => {
      provider?.awareness?.setLocalState(null);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [provider]);

  useEffect(() => {
    console.log("Is master client: " + isMasterClient);
  }, [isMasterClient]);

  useEffect(() => {
    console.log("Is Synced: " + isSynced);
    if (!isSynced) {
      setIsMasterClient(false);
    }
  }, [isSynced]);

  useEffect(() => {
    console.log("Status: " + status);
  }, [status]);

  return {
    ydoc,
    status,
    isSynced,
    inactivityDisc,
    provider,

    createComment,
    updateComment,
    resolveComment,
    activeComment,

    localComments,
    commentsInEditMode,

    setCommentsInEditMode,
    setActiveComment,
    setLocalComments,

    getComment,
    updateOrphanedComments,
    deleteComments,
    updateCommentRef,

    newComments,
    setNewComments,

    isMasterClient,
    isPendingSave
  };
};

interface ICollaborativeProps {
  roomName: string;
  isDisabled: boolean;
  // Optional: only the `CollaborativeProvider` wrapper renders children; the
  // `useCollaborativeProvider` hook (used directly by CollaborativeProseEditor)
  // takes only roomName + isDisabled.
  children?: ReactNode;
}

export const CollaborativeProvider = (props: ICollaborativeProps) => {
  const collaborativeProvider = useCollaborativeProvider(props);

  return (
    <CollaborativeContext.Provider value={collaborativeProvider}>
      {props.children}
    </CollaborativeContext.Provider>
  );
};

export type CollaborativeProps = ReturnType<typeof useCollaborativeProvider>;
export const CollaborativeContext = createContext<CollaborativeProps>(null);
export const useCollaborative = () => useContext(CollaborativeContext);
